import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { captureException } from '@sentry/nextjs';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  useGetSportsRatingScalesQuery,
  useUpdateCoachParamsMutation,
} from 'types/generated/client';
import { RatingUpdate, filterKeys } from 'utils/shared/coachBuilder';
import { calculateYearsOfCoachingExperience } from 'utils/shared/user/calculateYearsOfCoachingExperience';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import Button, { ButtonText } from 'components/Button';
import InputField from 'components/coaches-builder/InputField';
import { InputType } from 'components/coaches-builder/InputField/InputField';
import ProfileItem from 'components/coaches-builder/ProfileItem';
import Switcher from 'components/forms/Switcher';
import Modal from 'components/modals/Modal';
import { getExperienceSchema } from './schemas';
import { FormModalModeEnum, FormModalProps } from '../types';

interface ExperienceFormModalProps extends FormModalProps {}

const ExperienceFormModal: React.FC<ExperienceFormModalProps> = ({
  isOpen,
  handleClose,
  coachData,
  mode,
}) => {
  const { data: sportsRatingScalesData } = useGetSportsRatingScalesQuery();

  const [updateCoachParams, { loading: updateCoachParamsLoading }] = useUpdateCoachParamsMutation();

  const viewer = useViewer();

  const ratingSchema =
    sportsRatingScalesData?.pickleballRatingScales?.reduce((schemas, scale) => {
      schemas[`${scale.shortName}`] = z.union([z.number(), z.nan()]).optional();
      schemas[`has${scale.shortName}Id`] = z.boolean().optional();
      return schemas;
    }, {} as { [key: string]: z.ZodTypeAny }) || {};

  type ExperienceSchemaType = z.infer<ReturnType<typeof getExperienceSchema>>;

  const {
    register,
    formState: { errors },
    control,
    setValue,
    reset,
    watch,
    handleSubmit,
  } = useForm<ExperienceSchemaType>({
    resolver: ratingSchema ? zodResolver(getExperienceSchema(ratingSchema)) : undefined,
  });

  const onSubmit = async (data: ExperienceSchemaType) => {
    try {
      const ratingUpdateObject: RatingUpdate[] = [];

      if (data?.rating) {
        const ratingKeys = filterKeys(data.rating);
        Object.keys(ratingKeys).map((ratingKey) => {
          const filterRating = sportsRatingScalesData?.pickleballRatingScales?.find(
            (pickleballRatingScale) => {
              return pickleballRatingScale.shortName === ratingKey;
            },
          );

          if (
            data.rating[`has${ratingKey}Id`] &&
            !isNaN(ratingKeys[ratingKey]) &&
            ratingKeys[ratingKey] >= 0 &&
            filterRating?.id
          ) {
            ratingUpdateObject.push({
              rating: ratingKeys[ratingKey],
              pickleballRatingScaleId: filterRating?.id || '',
              userId: viewer?.userId || '',
            });
          }
        });
      }

      await updateCoachParams({
        variables: {
          userId: viewer.userId,
          isPciMember: data.hasPciId,
          // isPprMember: data.hasPprId,
          isQuatroPickleballMember: data.isQuatroPickleballMember,
          isIptpaMember: data.isIptpaMember,
          pickleRatingObjects: ratingUpdateObject,
          coachingStartYear:
            data?.coachingStartYear && data?.coachingStartYear > 0
              ? calculateYearsOfCoachingExperience(data?.coachingStartYear || 0)
              : undefined,
        },
      });

      handleClose(true);
    } catch (err) {
      captureException(err);
    }
  };

  useEffect(() => {
    // if (mode === FormModalModeEnum.Add) {
    //   reset();
    //   return;
    // }
    if (!coachData) {
      return;
    }
    setValue('hasPciId', coachData?.isPciMember ? true : false);

    // setValue('hasPprId', coachData?.isPprMember ? true : false);

    setValue('isIptpaMember', coachData?.isIptpaMember ? true : false);

    setValue('isQuatroPickleballMember', coachData?.isQuatroPickleballMember ? true : false);

    if (coachData?.coachingStartYear && coachData?.coachingStartYear > 0) {
      setValue(
        'coachingStartYear',
        calculateYearsOfCoachingExperience(coachData?.coachingStartYear || 0) || undefined,
      );
    }

    let rating: { [key: string]: any } = {};
    coachData?.pickleballRatingScales?.forEach((pickleballRatingScale) => {
      const shortName = pickleballRatingScale?.scale?.shortName;

      // Set the rating value and the existence flag
      rating[`has${shortName}Id`] = true;
      rating[shortName] = pickleballRatingScale.rating;
    });

    setValue('rating', rating);
  }, [coachData, reset, setValue, mode, sportsRatingScalesData]);

  return (
    <Modal isOpen={isOpen} handleClose={handleClose} classNameMaxWidth="max-w-[80vw]">
      <div className="flex items-center justify-between border-b px-8 py-4">
        <h2 className="typography-product-heading-desktop">My Experience</h2>
        <ButtonText onClick={() => handleClose()}>
          <CloseIcon className="h-4 w-4" />
        </ButtonText>
      </div>
      <div className="flex flex-col px-4 md:px-10">
        <ProfileItem
          label="How many years have you been coaching?"
          description="Give players an idea of your experience."
          component={
            <InputField
              fieldLabel=""
              errors={errors}
              placeholder="i.e. 5"
              name="coachingStartYear"
              register={register}
              inputProps={{ type: 'number' }}
              inputType={InputType.Regular}
            />
          }
        />

        {/* <ProfileItem
          label="Certifications"
          description="Select if you're a certified coach and optionally enter your ID."
          component={
            <div className="flex flex-col gap-8">
              <Switcher
                errors={errors}
                control={control}
                name={`isQuatroPickleballMember`}
                label="Quatro Pickleball"
              />
              <Switcher errors={errors} control={control} name={`isIptpaMember`} label="IPTPA" />

              <Switcher errors={errors} control={control} name={`hasPciId`} label="PCI" />
            </div>
          }
        /> */}

        <ProfileItem
          label="Player rating"
          description="Let players know your playing level."
          hasBottomBorder={false}
          component={
            <div className="flex flex-col gap-8">
              {sportsRatingScalesData?.pickleballRatingScales.map((scale, index) => {
                const hasRatingId = watch(`rating.has${scale.shortName}Id`);
                return (
                  <div className="flex flex-col gap-4" key={scale.id}>
                    <Switcher
                      errors={errors}
                      control={control}
                      name={`rating.has${scale.shortName}Id`}
                      label={scale.shortName}
                    />
                    {hasRatingId && (
                      <div className="w-full">
                        <InputField
                          fieldLabel=""
                          inputProps={{
                            type: 'number',
                            step: 'any',
                          }}
                          errors={errors}
                          placeholder={`${scale.shortName} Rating`}
                          name={`rating.${scale.shortName}`}
                          register={register}
                          inputType={InputType.Regular}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          }
        />
      </div>
      <div className=" flex justify-end border-t px-8 py-4">
        <Button
          disabled={updateCoachParamsLoading}
          variant="primary"
          onClick={handleSubmit(onSubmit)}
          className="w-fit"
          size="lg"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default ExperienceFormModal;
